import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { ActionIconHelper } from '../../../../../helper/action-icon.helper';
import { LeadHelper } from '../../../../../helper/lead.helper';
import { TitleHelper } from '../../../../../helper/title.helper';
import { LeadsData } from '../../../../../models/leads/leads.model';
import { FiltersAndSorting } from '../requests-overview-content.component';
import { SearchAdditionaryComponent } from '../../overview-table/search-additionary/search-additionary.component';
import { TablePopupComponent } from '../../overview-table/table-popup/table-popup.component';

@Component({
  selector: 'fi-requests-overview-desktop-container',
  templateUrl: './requests-overview-desktop-container.component.html',
  styleUrls: ['./requests-overview-desktop-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RequestsOverviewDesktopContainerComponent {
  @Input() filters: FiltersAndSorting;
  @Input() contactLeads: LeadsData[];
  @Input() checkedAll: boolean;
  @Input() filterType: string;
  @Input() showFilterSelectBox: boolean;
  @Input() compactView$: Observable<boolean>;
  @Input() checkedLeads: Set<number>;
  @Input() activeIndex = -1;
  @Input() activeCellHeight = '';
  @Input() userId: number;
  @Input() isFinstralUser: boolean;
  @Input() leadsCount = 0;
  @Input() visibleLeadsCount = 0;
  @Input() pagesCount = 0;
  @Input() searchFilterOpen = false;
  @Input() multiSelectDataLoaded = false;
  @Input() multiselectNameKey = '';
  @Input() multiselectSelectedOptions: any[] = [];
  @Input() multiselectOptions: any[] = [];
  @Input() showActions = false;
  @Input() expandedLeadLinkId = -1;
  @Input() getAddress: (lead: LeadsData, desktop?: boolean) => any;
  @Input() getLeadTakenMessage: (lead: LeadsData) => any;

  @Output() expandedLeadLinkIdChange = new EventEmitter<number>();
  @Output() showActionsChange = new EventEmitter<boolean>();
  @Output() checkAllFieldsRequested = new EventEmitter<boolean>();
  @Output() sortByColumnRequested = new EventEmitter<any>();
  @Output() openMultiselectFilterRequested = new EventEmitter<string>();
  @Output() openSearchFilterRequested = new EventEmitter<string>();
  @Output() multiselectChanged = new EventEmitter<string[]>();
  @Output() addFilterRequested = new EventEmitter<{ filterType: string; value: string }>();
  @Output() rejectAssignmentRequested = new EventEmitter<number>();
  @Output() confirmAssignmentRequested = new EventEmitter<number>();
  @Output() openLeadDetailsRequested = new EventEmitter<{ id: number; newTab: boolean }>();
  @Output() duplicateClickRequested = new EventEmitter<number>();
  @Output() checkLeadRequested = new EventEmitter<LeadsData>();
  @Output() openMapNewTabRequested = new EventEmitter<{ event: Event; lead: LeadsData }>();
  @Output() openSuggestedActionPopupRequested = new EventEmitter<number>();
  @Output() openActionPopupRequested = new EventEmitter<{ index: number; event: MouseEvent }>();
  @Output() closeOverlayRequested = new EventEmitter<boolean>();
  @Output() markAsUnreadRequested = new EventEmitter<number>();
  @Output() markAsReadRequested = new EventEmitter<number>();
  @Output() prevPageRequested = new EventEmitter<void>();
  @Output() nextPageRequested = new EventEmitter<void>();

  @ViewChild(TablePopupComponent) private tablePopupComponent: TablePopupComponent;

  readonly leadHelper = LeadHelper;
  readonly titleHelper = TitleHelper;
  readonly actionHelper = ActionIconHelper;
  readonly searchAdditionaryComponent = SearchAdditionaryComponent;

  checkAllFields(check: boolean): void { this.checkAllFieldsRequested.emit(check); }
  sortByColumn(sortColumn: any): void { this.sortByColumnRequested.emit(sortColumn); }
  openMultiselectFilter(type: string): void { this.openMultiselectFilterRequested.emit(type); }
  openSearchFilter(type: string): void { this.openSearchFilterRequested.emit(type); }
  onMultiselectChanged(value: string[]): void { this.multiselectChanged.emit(value); }
  addFilter(filterType: string, value: string): void { this.addFilterRequested.emit({ filterType, value }); }
  rejectAssignment(index: number): void { this.rejectAssignmentRequested.emit(index); }
  confirmAssignment(index: number): void { this.confirmAssignmentRequested.emit(index); }
  openLeadDetails(id: number, newTab = false): void { this.openLeadDetailsRequested.emit({ id, newTab }); }
  onDuplicateClick(index: number): void { this.duplicateClickRequested.emit(index); }
  checkLead(lead: LeadsData): void { this.checkLeadRequested.emit(lead); }
  openMapNewTab(event: Event, lead: LeadsData): void { this.openMapNewTabRequested.emit({ event, lead }); }
  openSuggestedActionPopup(index: number): void { this.openSuggestedActionPopupRequested.emit(index); }
  openActionPopup(index: number, event: MouseEvent): void { this.openActionPopupRequested.emit({ index, event }); }
  closeOverlay(reloadLeads = false): void { this.closeOverlayRequested.emit(reloadLeads); }
  markAsUnread(id: number): void { this.markAsUnreadRequested.emit(id); }
  markAsRead(id: number): void { this.markAsReadRequested.emit(id); }
  prevPage(): void { this.prevPageRequested.emit(); }
  nextPage(): void { this.nextPageRequested.emit(); }

  setExpandedLeadLinkId(leadId: number): void {
    this.expandedLeadLinkId = leadId;
    this.expandedLeadLinkIdChange.emit(leadId);
  }

  setShowActions(showActions: boolean): void {
    this.showActions = showActions;
    this.showActionsChange.emit(showActions);
  }

  getDuplicateColspan(buttonsCell = false): number {
    let colspan = 8;

    if ( window.innerWidth < 1024 ) {
      colspan = 7;
    } else if ( window.innerWidth < 1471 ) {
      colspan = 8;
    } else if ( window.innerWidth < 1590 ) {
      colspan = 7;
    }

    if ( buttonsCell ) {
      return 9 - colspan;
    }

    return colspan;
  }

  isOverflown(element: HTMLElement): boolean {
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
  }

  trackByLeadId = (_: number, item: any) => item?.Lead?.Id ?? _;

  trackByIndex = (index: number) => index;

  getDate(lead: LeadsData): string {
    if ( lead.Lead?.Number ) {
      return `${lead.Lead.Number.substring(6, 8)}.${lead.Lead.Number.substring(4, 6)}.${lead.Lead.Number.substring(0, 4)}`;
    }
    return '';
  }

  getAppointmentDate(lead: LeadsData): string {
    return this.formatDate(lead?.IsAppointment && lead.Lead?.Deadline ? lead.Lead.Deadline as any : null);
  }

  openSuggestedActionTablePopup(lead: any, suggestedAction: any): void {
    if ( !this.tablePopupComponent ) {
      return;
    }

    this.tablePopupComponent.lead = lead;
    this.tablePopupComponent.onBtnClick(suggestedAction);
  }

  private formatDate(date: Date | string | null): string {
    if ( !date ) {
      return '';
    }

    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).split(',')[0];
  }
}
